import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Title } from "@/mocks/titles";

const typeLabel: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
};

interface HeroCarouselProps {
  items: Title[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-foreground-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.banner}
            alt={`${item.name} - 蘑菇TV热播推荐`}
            title={`${item.name} + 蘑菇TV`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-end pb-10 md:pb-16">
        <div className="w-full max-w-3xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">
              {typeLabel[current.type]}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-white/15 backdrop-blur-sm text-background-50 text-xs">
              {current.region}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-white/15 backdrop-blur-sm text-background-50 text-xs">
              {current.year}
            </span>
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 leading-tight">
            {current.name}
          </h1>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-background-100 max-w-2xl leading-relaxed">
            {current.summary}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-background-100">
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-primary-400"></i>
              {current.rating > 0 ? current.rating.toFixed(1) : "暂无评分"}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-star-line"></i>
              {current.director}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {current.duration}
            </span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white/15 backdrop-blur-sm text-background-50 text-sm font-semibold hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apps-2-line text-lg"></i>
              更多{typeLabel[current.type]}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-6 right-4 md:right-8 flex items-center gap-2">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-background-50 hover:bg-white/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center text-background-50 hover:bg-white/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute z-20 bottom-6 left-4 md:left-8 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${item.name}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}